import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { switchMap, tap } from 'rxjs/operators';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SamwinLinesState } from '../samwin-lines/samwin-lines.state';
import { Skill } from '../samwin-skills/skill';
import { CallListChanged, SkillFilterChanged, UpdatePreviousCall } from './samwin-call-list.actions';

export interface SamwinCallListStateModel {
   previousCallInfo: SamwinCallInfoRemoteDto;
   list: SamwinCallInfoRemoteDto[];
   skillFilter: Skill;
}

@State<SamwinCallListStateModel>({
   name: 'samwinCallList',
   defaults: {
      previousCallInfo: null,
      list: [],
      skillFilter: null,
   },
})
@Injectable()
export class SamwinCallListState implements NgxsOnInit {
   constructor(private store: Store) {}

   @Selector([SamwinCallListState])
   public static getCallList(state: SamwinCallListStateModel): SamwinCallInfoRemoteDto[] {
      return state?.list;
   }

   @Selector([SamwinCallListState, SamwinCallListState.getCallList])
   public static getCallListFilteredBySkillFilter(state: SamwinCallListStateModel, calls: SamwinCallInfoRemoteDto[]): SamwinCallInfoRemoteDto[] {
      return calls.filter((c) => {
         return _.isNil(state.skillFilter) || state.skillFilter.skillId === c.queueId || state.skillFilter.skillId === c.queueIdOpExt;
      });
   }

   @Selector([SamwinCallListState])
   public static isCallListFilteredBySkillFilter(state: SamwinCallListStateModel): boolean {
      return !_.isNil(state.skillFilter);
   }

   @Selector([SamwinCallListState])
   public static getCallListSkillFilter(state: SamwinCallListStateModel): Skill {
      return state.skillFilter;
   }

   @Selector([SamwinCallListState.getCallList])
   public static getCallByPositionFn(callList: SamwinCallInfoRemoteDto[]): (position: number) => null | SamwinCallInfoRemoteDto {
      return (position: number) => {
         if (callList.length < position) {
            return null;
         }
         return callList[position - 1];
      };
   }

   @Selector([SamwinCallListState.getCallList])
   public static getCallByIdFn(callList: SamwinCallInfoRemoteDto[]): (callId: number) => null | SamwinCallInfoRemoteDto {
      return (callId: number) => {
         const call: SamwinCallInfoRemoteDto = callList.find((x) => x.callId === callId);
         if (_.isNil(call)) {
            return null;
         }
         return call;
      };
   }

   @Selector([SamwinCallListState])
   public static getCallInfoFromPreviousCall(state: SamwinCallListStateModel): SamwinCallInfoRemoteDto {
      return state.previousCallInfo;
   }

   @Selector([SamwinCallListState.getCallInfoFromPreviousCall])
   public static previousCallId(callInfo: SamwinCallInfoRemoteDto): number {
      return callInfo?.callId;
   }

   ngxsOnInit() {
      this.store
         .select(SamwinCallListState.getCallByIdFn)
         .pipe(
            switchMap((getCallById) =>
               this.store.select(SamwinLinesState.getSourceLineCallId).pipe(
                  tap((currentCallId) => {
                     const currentCallRemoteDto = getCallById(currentCallId);
                     if (!_.isNil(currentCallRemoteDto)) {
                        this.store.dispatch(new UpdatePreviousCall(currentCallRemoteDto));
                     }
                  })
               )
            )
         )
         .subscribe();
   }

   @Action(SkillFilterChanged)
   public skillFilterChanged(ctx: StateContext<SamwinCallListStateModel>, action: SkillFilterChanged) {
      ctx.patchState({
         skillFilter: action.skill,
      });
   }

   @Action(CallListChanged)
   public callListChange(ctx: StateContext<SamwinCallListStateModel>, action: CallListChanged) {
      ctx.patchState({
         list: action.updatedCallList,
      });
   }

   @Action(UpdatePreviousCall)
   public UpdatePreviousCall(ctx: StateContext<SamwinCallListStateModel>, action: UpdatePreviousCall) {
      ctx.patchState({
         previousCallInfo: action.currentCallRemoteDto,
      });
   }
}
